// ==========================================================================
// screens/MoneyReceipts.tsx — Money → Receipts (FF-044, 9 Oct 2026)
//   Drop photos or PDFs (or take a photo on the phone). Each lands in the inbox.
//   Fill in who, date, total and code — by hand now; "Read it" pre-fills once
//   the reader is switched on at go-live (FF-035), and you approve with Save.
//   A filled-in receipt then matches its bank line in Reconcile by amount.
//   "Paid by Coretel" receipts are kept for the record and never matched.
// ==========================================================================
import { useMemo, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { AlertTriangle, Camera, CheckCircle2, FileText, FileUp, Link2, Sparkle, Unlink } from "lucide-react";
import { Button, Card, ErrorText } from "@/components/ui";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import ContactPicker from "@/components/ContactPicker";
import { money, toCents, fromCents, useCategories, useTrackers, useAccounts } from "@/lib/money";
import AssetSelect from "@/components/AssetSelect";
import { assetKindFor } from "@/lib/property";
import { useContacts } from "@/lib/contacts";
import { splitDescription } from "@/lib/transactions";
import { readReceipt, useAddReceipts, useReceiptFile, useReceipts, useSaveReceipt, useWorkerHealth, type Receipt, type Suggested } from "@/lib/receipts";
import { supabase } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { CodePicker } from "./MoneyTransactions";

type Filter = "fill" | "bill" | "waiting" | "matched" | "company" | "all";
const stateOf = (r: Receipt): Exclude<Filter, "all"> =>
  r.transaction_id ? "matched" : r.paid_by !== "us" ? "company" : r.is_bill && !r.paid_on && r.total_cents && r.due_on ? "bill" : r.paid_on ? "matched" : r.total_cents && r.receipt_date ? "waiting" : "fill";
const LABEL: Record<Exclude<Filter, "all">, string> = { fill: "To fill in", bill: "Bills to pay", waiting: "Waiting for bank line", matched: "Matched", company: "Paid by Coretel / other" };

function Thumb({ r, big = false }: { r: Receipt; big?: boolean }) {
  const { url, err } = useReceiptFile(r);
  const cls = big ? "h-[70vh] w-full rounded-md bg-surface-raised" : "h-40 w-full rounded-t-lg bg-surface-raised";
  if (!r.uploaded || err) return <div className={`${cls} flex flex-col items-center justify-center gap-1 text-xs text-ink-muted`}><AlertTriangle className="h-5 w-5" />{r.uploaded ? "Can't load the file" : r.is_bill && !r.file_name ? "Typed in, no file" : "File didn't upload"}</div>;
  if (!url) return <div className={`${cls} animate-pulse`} />;
  if (r.mime === "application/pdf") return big ? <iframe title="Receipt" src={url} className={cls} /> : <div className={`${cls} flex flex-col items-center justify-center gap-1 text-ink-muted`}><FileText className="h-8 w-8" />PDF</div>;
  if (r.mime === "image/heic" || r.mime === "image/heif") return <div className={`${cls} flex flex-col items-center justify-center gap-1 text-xs text-ink-muted`}><FileText className="h-8 w-8" />HEIC photo<a href={url} download={r.file_name ?? "receipt.heic"} className="font-medium text-primary">Download to view</a></div>;
  return <img src={url} alt="Receipt" className={`${cls} ${big ? "object-contain" : "object-cover object-top"}`} />;
}

function useLinkedLine(id: string | null) {
  return useQuery({
    queryKey: ["txns", "one", id], enabled: !!id,
    queryFn: async () => {
      const { data } = await supabase.from("transactions").select("id,account_id,posted_on,amount_cents,description").eq("id", id!).maybeSingle();
      return data as { id: string; account_id: string; posted_on: string; amount_cents: number; description: string } | null;
    },
  });
}

function ReceiptSheet({ r, readerOn, onClose }: { r: Receipt; readerOn: boolean; onClose: () => void }) {
  const save = useSaveReceipt(); const cats = useCategories(); const trackers = useTrackers(); const contacts = useContacts(); const accounts = useAccounts();
  const linked = useLinkedLine(r.transaction_id);
  const [who, setWho] = useState<string | null>(r.contact_id);
  const [date, setDate] = useState(r.receipt_date ?? "");
  const [total, setTotal] = useState(fromCents(r.total_cents));
  const [gst, setGst] = useState(fromCents(r.gst_cents));
  const [cat, setCat] = useState(r.category_id ?? "");
  const [tracker, setTracker] = useState(r.tracker_id ?? "");
  const [asset, setAsset] = useState(r.asset_id ?? "");
  const [paidBy, setPaidBy] = useState(r.paid_by);
  const [note, setNote] = useState(r.note ?? "");
  const [isBill, setIsBill] = useState(!!r.is_bill); const [due, setDue] = useState(r.due_on ?? "");
  const [reading, setReading] = useState(false); const [readErr, setReadErr] = useState(""); const [sug, setSug] = useState<Suggested | null>(null);
  const catList = cats.data ?? [];
  const isTransport = !!assetKindFor(cat, catList);

  async function doRead() {
    setReading(true); setReadErr("");
    try {
      const s = await readReceipt(r.id); setSug(s);
      // Fill only empty boxes; nothing is saved until Save.
      if (!date && s.date) setDate(s.date);
      if (!total && typeof s.total === "number") setTotal(s.total.toFixed(2));
      if (!gst && typeof s.gst === "number") setGst(s.gst.toFixed(2));
      if (!cat && s.code) { const c = catList.find((x) => x.code === s.code); if (c) setCat(c.id); }
      if (!who && s.merchant) { const c = (contacts.data ?? []).find((x) => x.name.toLowerCase() === s.merchant!.toLowerCase() || x.match_texts.some((m) => s.merchant!.toLowerCase().includes(m))); if (c) setWho(c.id); }
      if (!note && s.summary) setNote(s.summary);
    } catch (e) { setReadErr((e as Error).message); } finally { setReading(false); }
  }

  async function submit() {
    await save.mutateAsync({ id: r.id, r: {
      contact_id: who, receipt_date: date || null, total_cents: toCents(total), gst_cents: toCents(gst),
      category_id: cat || null, tracker_id: tracker || null, asset_id: isTransport ? asset || null : null, paid_by: paidBy, note: note.trim() || null,
      is_bill: isBill, due_on: isBill ? due || null : null, paid_on: isBill ? r.paid_on ?? null : null,
    } });
    onClose();
  }
  const money$ = (v: string, set: (s: string) => void, placeholder = "0.00") => (
    <div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={v} onChange={(e) => set(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" placeholder={placeholder} /></div>
  );

  return (
    <Sheet wide title={r.file_name ?? "Receipt"} onClose={onClose}>
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Thumb r={r} big />
        <div className="space-y-4">
          <div className="rounded-lg bg-primary/[0.07] p-3 ring-1 ring-primary/30">
            <button type="button" disabled={!readerOn || reading} onClick={doRead} className={`flex w-full items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold ${readerOn ? "bg-primary text-white" : "bg-surface-raised text-ink-muted"}`}>
              <Sparkle className="h-4 w-4" /> {reading ? "Reading…" : "Read it"}
            </button>
            <p className="mt-1.5 text-center text-xs text-ink-muted">{readerOn ? "Fills the empty boxes. Check them, then Save." : "The reader switches on at go-live. Type it in for now."}</p>
            {readErr && <p className="mt-1 text-center text-xs text-bad">{readErr}</p>}
            {sug?.card_last4 && <p className="mt-1 text-center text-xs text-ink-muted">Card on the receipt ends {sug.card_last4}.</p>}
          </div>
          <Lbl label="Who"><ContactPicker value={who} contacts={contacts.data ?? []} onChange={(id) => setWho(id)} newName={sug?.merchant ?? ""} /></Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Lbl>
            <Lbl label="Total">{money$(total, setTotal)}</Lbl>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="GST (optional)">{money$(gst, setGst)}</Lbl>
            <Lbl label="Paid by">
              <select value={paidBy} onChange={(e) => setPaidBy(e.target.value as Receipt["paid_by"])} className={inputCls}>
                <option value="us">Us</option><option value="company">Coretel card</option><option value="other">Someone else</option>
              </select>
            </Lbl>
          </div>
          <Lbl label="Budget code"><CodePicker key={cat || "none"} value={cat} cats={catList} sign={-1} onChange={setCat} startOpen={false} /></Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Tracker"><select value={tracker} onChange={(e) => setTracker(e.target.value)} className={inputCls}><option value="">None</option>{trackers.data?.filter((x) => !x.archived_at).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></Lbl>
            {isTransport && <Lbl label={assetKindFor(cat, catList) === "vehicle" ? "Vehicle" : "For which property"}><AssetSelect categoryId={cat} cats={catList} value={asset || null} onChange={(id) => setAsset(id ?? "")} /></Lbl>}
          </div>
          <Lbl label="Note"><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="What was it for?" /></Lbl>
          <div className="space-y-2 rounded-lg bg-surface-raised p-3">
            <label className="flex items-center justify-between gap-3 text-sm"><span><b>It's a bill to pay</b><span className="block text-xs text-ink-muted">Shows in Bills to pay until it's matched to the payment or marked paid.</span></span><input type="checkbox" checked={isBill} onChange={(e) => setIsBill(e.target.checked)} className="h-5 w-5 accent-primary" /></label>
            {isBill && <Lbl label="Due"><input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} /></Lbl>}
            {isBill && r.paid_on && !r.transaction_id && <p className="text-xs text-good">Marked paid {format(parseISO(r.paid_on), "d MMM")}. <button type="button" className="underline" onClick={async () => { await save.mutateAsync({ id: r.id, r: { paid_on: null } }); onClose(); }}>Undo</button></p>}
            {isBill && !r.paid_on && !r.transaction_id && <button type="button" onClick={async () => { await save.mutateAsync({ id: r.id, r: { is_bill: true, due_on: due || null, paid_on: new Date().toISOString().slice(0, 10) } }); onClose(); }} className="text-xs font-semibold text-primary">Mark it paid now</button>}
          </div>
          {paidBy !== "us" && <p className="text-xs text-ink-muted">Kept for the record. It won't be matched to our bank lines.</p>}
          <ErrorText>{save.error ? "Couldn't save. Try again." : null}</ErrorText>
          <Button disabled={save.isPending} onClick={submit}>{save.isPending ? "Saving…" : "Save"}</Button>

          {r.transaction_id && (
            <div className="space-y-2 rounded-lg bg-good/10 p-3 text-sm">
              <div className="flex items-center gap-2 font-semibold text-good"><Link2 className="h-4 w-4" /> Matched to a bank line</div>
              {linked.data && <p>{splitDescription(linked.data.description).merchant} · {format(parseISO(linked.data.posted_on), "d MMM")} · {accounts.data?.find((a) => a.id === linked.data!.account_id)?.name} · <span className="num">{money(Number(linked.data.amount_cents), { cents: true })}</span></p>}
              <button type="button" onClick={async () => { await save.mutateAsync({ id: r.id, r: { transaction_id: null } }); onClose(); }} className="flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-bad"><Unlink className="h-3.5 w-3.5" /> Unlink</button>
            </div>
          )}
          <Button type="button" variant="ghost" onClick={async () => { await save.mutateAsync({ id: r.id, r: { archived_at: r.archived_at ? null : new Date().toISOString() } }); onClose(); }}>{r.archived_at ? "Bring back" : "Archive (not needed)"}</Button>
        </div>
      </div>
    </Sheet>
  );
}

export default function MoneyReceipts() {
  const receipts = useReceipts(); const add = useAddReceipts(); const health = useWorkerHealth();
  const contacts = useContacts(); const cats = useCategories();
  const [filter, setFilter] = useState<Filter>("fill");
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const live = (receipts.data ?? []).filter((r) => !r.archived_at);
  const counts = useMemo(() => {
    const c = { fill: 0, bill: 0, waiting: 0, matched: 0, company: 0, all: live.length } as Record<Filter, number>;
    for (const r of live) c[stateOf(r)]++;
    return c;
  }, [live]);
  const list = live.filter((r) => filter === "all" || stateOf(r) === filter);
  const openR = (receipts.data ?? []).find((r) => r.id === open);
  const workerUp = health.data?.ok;

  const take = (files: FileList | null) => { if (files?.length) add.mutate(Array.from(files)); };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files); };

  return (
    <div className="space-y-6">
      {health.data && !workerUp && (
        <div className="flex gap-3 rounded-lg bg-warn/10 p-4 text-sm text-warn">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div><div className="font-semibold">The file service isn't running.</div>Receipts need it to store the photos. In a PowerShell window at C:\dev\family-app run <code className="rounded bg-surface px-1">pnpm dev:worker</code>, then refresh.</div>
        </div>
      )}

      <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition ${drag ? "border-primary bg-primary/5" : "border-line hover:border-primary/50"} ${!workerUp ? "pointer-events-none opacity-50" : ""}`}>
        <span className="flex gap-3 text-primary"><Camera className="h-7 w-7 lg:hidden" /><FileUp className="h-7 w-7" /></span>
        <span className="font-medium">{add.isPending ? "Adding…" : <><span className="lg:hidden">Take a photo or choose files</span><span className="hidden lg:inline">Drop receipts here, or click to choose</span></>}</span>
        <span className="text-xs text-ink-muted">Photos or PDFs, as many as you like. They land in "To fill in".</span>
        <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
      </label>
      <ErrorText>{add.error ? (add.error as Error).message : null}</ErrorText>

      <div className="flex flex-wrap gap-2">
        {(["fill", "bill", "waiting", "matched", "company", "all"] as Filter[]).map((k) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full px-3.5 py-1.5 text-sm ${filter === k ? "bg-ink font-semibold text-bg" : "bg-surface text-ink-muted ring-1 ring-line hover:text-ink"}`}>
            {k === "all" ? "All" : LABEL[k]} <span className="num ml-1 opacity-70">{counts[k]}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <Card className="py-10 text-center text-ink-muted">
          {live.length === 0 ? "No receipts yet. Drop the first one in above." : filter === "fill" ? "Nothing to fill in." : filter === "waiting" ? <>Nothing waiting. Filled-in receipts wait here until their bank line is imported, then match in <Link to="/money/reconcile" className="font-medium text-primary">Reconcile</Link>.</> : "Nothing here."}
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((r) => { const st = stateOf(r); const who = contacts.data?.find((c) => c.id === r.contact_id); const c = cats.data?.find((x) => x.id === r.category_id); return (
            <button key={r.id} type="button" onClick={() => setOpen(r.id)} className="overflow-hidden rounded-lg bg-surface text-left shadow-soft ring-1 ring-line/70 transition hover:ring-primary/40">
              <Thumb r={r} />
              <div className="space-y-1 p-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate font-semibold">{who?.name ?? r.file_name ?? "Receipt"}</span>
                  <span className="num shrink-0 font-semibold">{r.total_cents ? money(r.total_cents, { cents: true }) : "—"}</span>
                </div>
                <div className="text-xs text-ink-muted">{r.receipt_date ? format(parseISO(r.receipt_date), "EEE d MMM yyyy") : "No date yet"}{c ? ` · ${c.code} ${c.name}` : ""}</div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${st === "matched" ? "bg-good/10 text-good" : st === "fill" ? "bg-warn/10 text-warn" : st === "company" ? "bg-surface-raised" : "bg-info/10 text-info"}`}>
                  {st === "matched" && <CheckCircle2 className="h-3 w-3" />}{LABEL[st]}
                </span>
              </div>
            </button>
          ); })}
        </div>
      )}
      {openR && <ReceiptSheet key={openR.id} r={openR} readerOn={!!health.data?.reader} onClose={() => setOpen(null)} />}
    </div>
  );
}
